import type { NotificationGroupProps } from '../components';
import type { BackendNotificationGroup } from '../types/notification-types';
import type { NotificationItemProps } from '@tasama/ui';
import { formatDate } from '@tasama/utils';
import { i18n } from '@tasama/i18n';

/**
 * Determine unread status from backend status.
 * Only when status is exactly 'read' (case-insensitive) it's considered read.
 */
function isUnreadFromStatus(status?: string | null): boolean {
  if (!status) return true;
  return status.toLowerCase() !== 'read';
}

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Riyadh';
  } catch {
    return 'Asia/Riyadh';
  }
}

export function formatNotificationTime(
  createdAt: string,
  timeZone?: string
): string {
  try {
    // Ensure UTC format
    const utcString = createdAt.endsWith('Z') ? createdAt : `${createdAt}Z`;
    const date = new Date(utcString);

    // Validate date
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }

    const lang = i18n.language;
    // ar-SA-u-nu-latn: Arabic locale (for ص/م AM/PM) with Latin numerals forced
    const detectedLocale = lang === 'ar' ? 'ar-SA-u-nu-latn' : 'en-GB';

    const detectedTimeZone = timeZone || getBrowserTimeZone();

    // Format to localized time
    return date.toLocaleString(detectedLocale, {
      timeZone: detectedTimeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'Invalid time';
  }
}

/**
 * Convert backend notification data to frontend format
 */
export function convertBackendToFrontend(
  backendGroups: BackendNotificationGroup[],
  serviceTitles?: Record<string, string> | null
): NotificationGroupProps[] {
  if (!Array.isArray(backendGroups)) return [];
  return backendGroups.map((group) => {
    const mapped = group.notifications.map((notification) => {
      const resolvedArgs: Record<string, string> = {
        ...notification.messageArgs,
      };
      if (resolvedArgs.serviceName) {
        resolvedArgs.serviceName =
          serviceTitles?.[resolvedArgs.serviceName] ?? resolvedArgs.serviceName;
      }

      if (resolvedArgs.stepName) {
        resolvedArgs.stepName =
          serviceTitles?.[resolvedArgs.stepName] ?? resolvedArgs.stepName;
      }
      if (resolvedArgs.statusKey) {
        resolvedArgs.status = i18n.t(
          `business-landing:${resolvedArgs.statusKey}`
        );
      }
      return {
        notification: {
          notificationId: notification.notificationId,
          actions: notification.actions,
          title: i18n.t(`portal:${notification.titleKey}`, resolvedArgs),
          message: i18n.t(`portal:${notification.messageKey}`, resolvedArgs),
          entityType: notification.entityType,
          entityId: notification.entityId,
          priority: notification.priority,
          notificationCategory: notification.notificationCategory,
          roles: notification.roles,
          createdAt: notification.createdAt,
          status: notification.status,
          readAt: notification.readAt,
          deleted: notification.deleted,
          groupLabel: notification.groupLabel,
        },
        time: formatNotificationTime(notification.createdAt),
        isUnread: isUnreadFromStatus(notification.status),
        isHighlighted: false,
        testID: `notification-${notification.entityId}`,
      };
    });

    // Sort notifications by createdAt DESC (newest first)
    mapped.sort((a, b) => {
      const aTime = new Date(a.notification.createdAt).getTime();
      const bTime = new Date(b.notification.createdAt).getTime();
      return bTime - aTime;
    });

    return {
      label:
        group.dateHeader === 'Today'
          ? i18n.t('portal:notifications.today')
          : group.dateHeader === 'Yesterday'
            ? i18n.t('portal:notifications.yesterday')
            : formatDate(group.dateHeader),
      notifications: mapped,
    };
  });
}

/**
 * Helper to extract groups from page data
 * Handles both wrapped { data: [...] } and direct array response
 */
export function getGroupsFromPage(
  page: { data?: BackendNotificationGroup[] } | BackendNotificationGroup[]
): BackendNotificationGroup[] {
  if (Array.isArray(page)) {
    return page;
  }
  return page.data ?? [];
}

/**
 * Merge and process notification groups from multiple pages
 * Handles deduplication and sorting of notifications within groups
 */
export function mergeNotificationGroups(
  pages: any[] | undefined,
  serviceTitles?: Record<string, string> | null
): NotificationGroupProps[] {
  if (!pages) return [];

  const mergedGroups: NotificationGroupProps[] = [];

  pages.forEach((page) => {
    const groupsData = getGroupsFromPage(page);
    const convertedGroups = convertBackendToFrontend(groupsData, serviceTitles);
    convertedGroups.forEach((newGroup) => {
      const existingGroupIndex = mergedGroups.findIndex(
        (group) => group.label === newGroup.label
      );

      if (existingGroupIndex >= 0) {
        const existingGroup = mergedGroups[existingGroupIndex]!;
        const existingNotifications = existingGroup.notifications;
        const newNotifications = newGroup.notifications.filter(
          (newNotif: NotificationItemProps) =>
            !existingNotifications.some(
              (existingNotif: NotificationItemProps) =>
                existingNotif.notification.notificationId ===
                newNotif.notification.notificationId
            )
        );
        existingGroup.notifications.push(...newNotifications);
        // Ensure newest-first ordering within the label
        existingGroup.notifications.sort(
          (a: NotificationItemProps, b: NotificationItemProps) => {
            const aTime = new Date(a.notification.createdAt).getTime();
            const bTime = new Date(b.notification.createdAt).getTime();
            return bTime - aTime;
          }
        );
      } else {
        mergedGroups.push(newGroup);
      }
    });
  });

  return mergedGroups;
}
